import React, { useState, useEffect, useCallback, useRef } from 'react';
import GetCookie from '../../utils/GetCookie';
import TrimContent from '../../utils/TrimContent';
import ThemedSelect from '../../utils/ThemedSelect';
import { getInitials, getRandomColor } from '../../utils/UserCustomDisplay';
import axios from 'axios';

const API_BASE_URL = '/pceapi/administration';

const ASSET_PAGE_SIZE = 25;

const SELECT_ALL_PAGE_SIZE = 200;

const PROJECT_ROLE_LABELS = {
  PROJECT_MANAGER: 'Project Manager',
  DOSSIER_DEV_LEAD: 'Dossier Development Lead',
  DOSSIER_VIEWER: 'Dossier Viewer'
};

const PLATFORM_ROLE_LABELS = {
  ADMIN: 'Admin',
  GLOBAL_VIEWER: 'Global Viewer',
  COUNTRY_VIEWER: 'Country Viewer',
  SUPER_USER: 'Super User',
  STANDARD_USER: 'Standard User'
};

const roleLabel = (value) => PLATFORM_ROLE_LABELS[value] || value || 'None';

const COUNT_ROWS = [
  ['projects', 'Projects'],
  ['data_collections', 'Data Folders'],
  ['country_headships', 'Country headships'],
  ['genbi_templates', 'Dossiers'],
  ['genbi_reports', 'Reports']
];

const TransferOwnershipModal = ({ show, onClose, sourceUser,
                                  onTransferred, onError, onWarning, onProcessing }) => {
  const accessToken = GetCookie('access_token');

  const [activeTab, setActiveTab] = useState('partial'); // 'partial' | 'complete'

  const [summary, setSummary] = useState(null);
  const [loadingSummary, setLoadingSummary] = useState(false);

  const [targetUserId, setTargetUserId] = useState('');

  // Partial tab
  const [assetView, setAssetView] = useState('PROJECT'); // 'PROJECT' | 'DATA_COLLECTION'
  const [assets, setAssets] = useState([]);
  const [assetTotal, setAssetTotal] = useState(0);
  const [assetPage, setAssetPage] = useState(1);
  const [assetSearchInput, setAssetSearchInput] = useState('');
  const [assetSearch, setAssetSearch] = useState('');
  const [loadingAssets, setLoadingAssets] = useState(false);
  const [selectingAll, setSelectingAll] = useState(false);

  const [selectedFabrics, setSelectedFabrics] = useState([]);
  const [selectedThreads, setSelectedThreads] = useState([]);

  const [isSubmitting, setIsSubmitting] = useState(false);
  // Second step on the complete transfer, which is irreversible.
  const [confirmingComplete, setConfirmingComplete] = useState(false);

  const headers = { Authorization: accessToken };

  const assetCache = useRef(new Map());

  const clearAssetCache = useCallback(() => {
    assetCache.current.clear();
  }, []);

  const reportFailure = useCallback((message) => {
    if (onError) onError(message);
  }, [onError]);

  const reportWarning = useCallback((message) => {
    if (onWarning) onWarning(message);
    else if (onError) onError(message);
  }, [onWarning, onError]);

  const fetchSummary = useCallback(async () => {
    if (!sourceUser) return;
    setLoadingSummary(true);
    try {
      const response = await axios.get(
        `${API_BASE_URL}/iam-users/${sourceUser.user_id}/ownership-summary`,
        { headers }
      );
      const data = response.data;
      if (data.status !== 'success') {
        reportFailure(data.message || 'Failed to load ownership summary');
        return;
      }
      setSummary(data.data);
    } catch (error) {
      console.error('Error loading ownership summary:', error);
      reportFailure(error.message || 'Failed to load ownership summary');
    } finally {
      setLoadingSummary(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sourceUser, accessToken, reportFailure]);

  const fetchAssets = useCallback(async () => {
    if (!sourceUser) return;

    const cacheKey = `${assetView}|${assetPage}|${assetSearch}`;
    const cached = assetCache.current.get(cacheKey);
    if (cached) {
      setAssets(cached.data);
      setAssetTotal(cached.total);
      return;
    }

    setLoadingAssets(true);
    try {
      const response = await axios.post(
        `${API_BASE_URL}/iam-users/${sourceUser.user_id}/ownership-assets`,
        {
          asset_type: assetView,
          page: assetPage,
          size: ASSET_PAGE_SIZE,
          search: assetSearch || undefined
        },
        { headers }
      );
      const data = response.data;
      if (data.status !== 'success') {
        reportFailure(data.message || 'Failed to load items');
        return;
      }
      const rows = data.data || [];
      const total = data.total || 0;
      setAssets(rows);
      setAssetTotal(total);
      assetCache.current.set(cacheKey, { data: rows, total });
    } catch (error) {
      console.error('Error loading ownership assets:', error);
      reportFailure(error.message || 'Failed to load items');
    } finally {
      setLoadingAssets(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sourceUser, assetView, assetPage, assetSearch, accessToken, reportFailure]);

  const resetState = useCallback(() => {
    clearAssetCache();
    setActiveTab('partial');
    setTargetUserId('');
    setAssetView('PROJECT');
    setAssets([]);
    setAssetTotal(0);
    setAssetPage(1);
    setAssetSearchInput('');
    setAssetSearch('');
    setSelectedFabrics([]);
    setSelectedThreads([]);
    setSelectingAll(false);
    setIsSubmitting(false);
    setConfirmingComplete(false);
    setSummary(null);
  }, [clearAssetCache]);

  useEffect(() => {
    if (show && sourceUser) {
      resetState();
      fetchSummary();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [show, sourceUser && sourceUser.user_id]);

  // Reload what the source user holds right now. Their memberships can change
  // while this modal sits open (someone removes them from a project), so the
  // cached pages are dropped and the current page is fetched again.
  const reloadOwnership = useCallback(async () => {
    clearAssetCache();
    if (assetPage !== 1) {
      // The page effect refetches once assetPage changes.
      setAssetPage(1);
      await fetchSummary();
    } else {
      await Promise.all([fetchSummary(), fetchAssets()]);
    }
  }, [clearAssetCache, assetPage, fetchSummary, fetchAssets]);

  // Coming back to this window after a while: quietly refresh, so the admin
  // doesn't pick from a list that has gone out of date. Held in a ref so the
  // listener always calls the latest version.
  const reloadOnFocus = useRef(null);
  reloadOnFocus.current = () => {
    if (!isSubmitting && !selectingAll) reloadOwnership();
  };
  useEffect(() => {
    if (!show) return undefined;
    const handleFocus = () => reloadOnFocus.current && reloadOnFocus.current();
    window.addEventListener('focus', handleFocus);
    return () => window.removeEventListener('focus', handleFocus);
  }, [show]);

  const hasSummary = summary !== null;

  useEffect(() => {
    if (show && sourceUser && hasSummary && activeTab === 'partial') {
      fetchAssets();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [show, hasSummary, assetView, assetPage, assetSearch, activeTab]);

  if (!show || !sourceUser) return null;

  const allTargets = (summary && summary.eligible_targets) || [];
  const eligibleTargets = activeTab === 'complete'
    ? allTargets.filter(u => u.can_receive_complete)
    : allTargets;
  const counts = (summary && summary.counts) || {};
  const solePmProjects = (summary && summary.sole_project_manager_of) || [];

  const source = (summary && summary.source) || sourceUser;

  const targetUser = eligibleTargets.find(u => u.user_id === targetUserId) || null;

  const linkedThreadIds = [...new Set(
    selectedFabrics.flatMap(f => f.linkedThreadIds || [])
  )];

  const linkedThreads = [...new Map(
    selectedFabrics.flatMap(f => f.linkedThreads || []).map(t => [t.id, t])
  ).values()].sort((a, b) => String(a.name).localeCompare(String(b.name)));

  const explicitThreads = selectedThreads.filter(t => !linkedThreadIds.includes(t.id));

  const selected = assetView === 'PROJECT' ? selectedFabrics : explicitThreads;
  const setSelected = assetView === 'PROJECT' ? setSelectedFabrics : setSelectedThreads;
  const selectedIds = selected.map(s => s.id);
  const totalPages = Math.max(Math.ceil(assetTotal / ASSET_PAGE_SIZE), 1);
  const pageIds = assets.map(a => a.id);

  const lockedIds = assetView === 'DATA_COLLECTION' ? linkedThreadIds : [];

  const selectableTotal = Math.max(assetTotal - lockedIds.length, 0);
  const allMatchingSelected = selectableTotal > 0 && selected.length >= selectableTotal
    && pageIds.every(id => selectedIds.includes(id) || lockedIds.includes(id));

  const toggleAsset = (asset) => {
    setSelected(prev =>
      prev.some(s => s.id === asset.id)
        ? prev.filter(s => s.id !== asset.id)
        : [...prev, { id: asset.id, name: asset.name,
                      linkedThreadIds: asset.linked_thread_ids || [],
                      linkedThreads: asset.linked_threads || [] }]
    );
  };

  const toggleAll = async () => {
    if (allMatchingSelected) {
      setSelected([]);
      return;
    }

    if (assetTotal <= assets.length) {
      setSelected(assets
        .filter(a => !lockedIds.includes(a.id))
        .map(a => ({ id: a.id, name: a.name,
                       linkedThreadIds: a.linked_thread_ids || [],
                       linkedThreads: a.linked_threads || [] })));
      return;
    }

    setSelectingAll(true);
    try {
      const collected = [];
      let page = 1;
      for (;;) {
        const response = await axios.post(
          `${API_BASE_URL}/iam-users/${sourceUser.user_id}/ownership-assets`,
          {
            asset_type: assetView,
            page,
            size: SELECT_ALL_PAGE_SIZE,
            search: assetSearch || undefined
          },
          { headers }
        );
        const data = response.data;
        if (data.status !== 'success') {
          reportFailure(data.message || 'Failed to select all items');
          return;
        }
        const batch = data.data || [];
        collected.push(...batch
          .filter(a => !lockedIds.includes(a.id))
          .map(a => ({ id: a.id, name: a.name,
                       linkedThreadIds: a.linked_thread_ids || [],
                       linkedThreads: a.linked_threads || [] })));
        if (batch.length < SELECT_ALL_PAGE_SIZE) break;
        page += 1;
      }
      setSelected(collected);
    } catch (error) {
      console.error('Error selecting all items:', error);
      reportFailure(error.message || 'Failed to select all items');
    } finally {
      setSelectingAll(false);
    }
  };

  const removeSelected = (view, id) => {
    const setter = view === 'PROJECT' ? setSelectedFabrics : setSelectedThreads;
    setter(prev => prev.filter(s => s.id !== id));
  };

  const runSearch = () => {
    setAssetSearch(assetSearchInput);
    setAssetPage(1);
  };

  const switchAssetView = (view) => {
    setAssetView(view);
    setAssetPage(1);
    setAssetSearchInput('');
    setAssetSearch('');
  };

  const submitTransfer = async (transferType) => {
    if (!targetUserId) return;

    setIsSubmitting(true);
    if (onProcessing) onProcessing('Transferring ownership');
    try {
      const response = await axios.post(
        `${API_BASE_URL}/iam-users/transfer-ownership`,
        {
          source_user_id: sourceUser.user_id,
          target_user_id: targetUserId,
          transfer_type: transferType,
          fabric_ids: transferType === 'PARTIAL' ? selectedFabrics.map(f => f.id) : undefined,
          thread_ids: transferType === 'PARTIAL' ? explicitThreads.map(t => t.id) : undefined
        },
        { headers }
      );
      const result = response.data;

      if (result.status !== 'success') {
        if (result.code === 'SELECTION_CHANGED') {
          // What was selected no longer belongs to this user (removed from
          // the project, or access revoked, after the list loaded). Drop those
          // items, reload the list and explain, instead of leaving a stale
          // selection that fails the same way again.
          const skipped = result.data || {};
          const goneFabrics = skipped.skipped_project_ids || [];
          const goneThreads = skipped.skipped_data_collection_ids || [];
          setSelectedFabrics(prev => prev.filter(f => !goneFabrics.includes(f.id)));
          setSelectedThreads(prev => prev.filter(th => !goneThreads.includes(th.id)));
          await reloadOwnership();
          reportWarning(result.message);
          return;
        }
        reportFailure(result.message || 'Failed to transfer ownership');
        return;
      }

      if (transferType === 'COMPLETE') {
        // Nothing is left to hand over, so there is no reason to stay here.
        if (onTransferred) onTransferred(result.message);
        onClose();
        return;
      }

      setSelectedFabrics([]);
      setSelectedThreads([]);
      setAssetPage(1);

      clearAssetCache();
      await Promise.all([fetchSummary(), fetchAssets()]);
      if (onTransferred) onTransferred(result.message);
    } catch (error) {
      console.error('Transfer ownership error:', error);
      reportFailure(error.message || 'Failed to transfer ownership');
    } finally {
      setIsSubmitting(false);
      if (onProcessing) onProcessing(null);
    }
  };

  const renderTargetPicker = () => (
    <>
      <label className="ev-reg-p-dark mb-1">
        Transfer to <span className="text-danger">*</span>
      </label>
      <ThemedSelect
        value={targetUserId}
        ariaLabel="Transfer ownership to"
        placeholder={loadingSummary
          ? 'Loading users'
          : eligibleTargets.length === 0
            ? 'No eligible user available'
            : 'Select user'}
        disabled={loadingSummary || eligibleTargets.length === 0}
        enableSearch={true}
        searchPlaceholder="Search user"
        options={eligibleTargets.map(u => ({
          value: u.user_id,
          label: u.name,
          hint: u.email,
          badge: roleLabel(u.role),
          avatarText: getInitials(u.email || u.name),
          avatarColor: getRandomColor(u.email || u.name || ''),
        }))}
        onChange={(nextId) => {
          setTargetUserId(nextId);
          // A pending confirmation names the old target, so it no longer applies.
          setConfirmingComplete(false);
        }}
      />

      {/* Why somebody might be missing from the list above. */}
      {!loadingSummary && activeTab === 'complete' && source.role !== 'STANDARD_USER' && (
        <p className="ev-fs-11 mt-1 mb-0">
          A complete transfer moves country headships, which a Standard User cannot
          hold so Standard Users are not listed here.
        </p>
      )}
      {!loadingSummary && eligibleTargets.length === 0 && (
        <p className="text-danger mt-1 mb-0">
          <i className="bi bi-exclamation-triangle me-1"></i>
          There is no other user who can receive this user's ownership.
        </p>
      )}
    </>
  );

  const renderPartialTab = () => (

    <div className="row g-3">
      <div className="col-12 col-md-7">
        <label className="ev-reg-p-dark mb-1">
          What to transfer <span className="text-danger">*</span>
        </label>
        <div className="d-flex gap-2 mb-3">
          <button
            type="button"
            className={`btn ${assetView === 'PROJECT' ? 'ev-btn-skyblue-fill-round' : 'ev-btn-blue-outline-round'} ev-btn-small`}
            onClick={() => switchAssetView('PROJECT')}
          >
            Projects{counts.projects != null && (
              <span className="ev-fs-11"> ({counts.projects})</span>
            )}
          </button>
          <button
            type="button"
            className={`btn ${assetView === 'DATA_COLLECTION' ? 'ev-btn-skyblue-fill-round' : 'ev-btn-blue-outline-round'} ev-btn-small`}
            onClick={() => switchAssetView('DATA_COLLECTION')}
          >
            Data Folders{counts.data_collections != null && (
              <span className="ev-fs-11"> ({counts.data_collections})</span>
            )}
          </button>
        </div>

        <div className="position-relative mb-3">
          <input
            type="text"
            className="form-control search-input ms-0"
            placeholder={assetView === 'PROJECT' ? 'Search projects' : 'Search Data Folders'}
            value={assetSearchInput}
            onChange={(e) => setAssetSearchInput(e.target.value)}
            onKeyPress={(e) => { if (e.key === 'Enter') runSearch(); }}
          />
          <button className="ev-btn-lightblue-fill-round search-bar-button" onClick={runSearch}>
            <i className="bi bi-search"></i>
          </button>
        </div>

        <div className="ev-card-2 scrollable-element mb-3" style={{ maxHeight: '300px', height: 'auto' }}>
          {loadingAssets || !hasSummary ? (
            <p className="ev-small-p-dark mb-0">
              <span className="spinner-border spinner-border-sm me-2" role="status"></span>
              Loading
            </p>
          ) : assets.length === 0 ? (
            <p className="ev-small-p-dark mb-0">
              {assetSearch ? 'Nothing matches that search' : 'Nothing here belongs to this user'}
            </p>
          ) : (
            <>
              <div
                className="ev-select-row border-bottom mb-1"
                role="button"
                onClick={toggleAll}
              >
                <input
                  className="form-check-input"
                  type="checkbox"
                  checked={allMatchingSelected}
                  disabled={selectingAll}
                  onChange={toggleAll}
                  onClick={(e) => e.stopPropagation()}
                />
                <span className="ev-select-row-text ev-bold-reg">
                  {selectingAll
                    ? 'Selecting'
                    : allMatchingSelected
                      ? `Clear all (${assetTotal})`
                      : `Select all (${assetTotal})`}
                </span>
                {selected.length > 0 && (
                  <span className="ms-auto ev-fs-11">{selected.length} selected</span>
                )}
              </div>
              {assets.map(asset => {
                const viaProject = assetView === 'DATA_COLLECTION'
                  && linkedThreadIds.includes(asset.id);
                return (
                <div
                  key={asset.id}
                  className={`ev-select-row ${selectedIds.includes(asset.id) ? 'selected' : ''}`}
                  role={viaProject ? undefined : 'button'}
                  onClick={viaProject ? undefined : () => toggleAsset(asset)}
                >
                  <input
                    className="form-check-input"
                    type="checkbox"
                    checked={viaProject || selectedIds.includes(asset.id)}
                    disabled={viaProject}
                    onChange={() => toggleAsset(asset)}
                    onClick={(e) => e.stopPropagation()}
                  />
                  <span className="ev-select-row-text">
                    <TrimContent text={asset.name} maxLength={38} />
                  </span>
                  <span className="ms-auto text-nowrap">
                    {viaProject && (
                      <span className="ev-message-card1 ev-fs-11 me-1">via project</span>
                    )}
                    {asset.country_name && (
                      <span className="badge bg-secondary ev-btn-small me-1">{asset.country_name}</span>
                    )}
                    {asset.is_creator && (
                      <span className="ev-message-card1 ev-fs-11 me-1">Creator</span>
                    )}
                    {asset.project_role && (
                      <span className="ev-message-card1 ev-fs-11 me-1">
                        {PROJECT_ROLE_LABELS[asset.project_role] || asset.project_role}
                      </span>
                    )}
                    {assetView === 'DATA_COLLECTION' && asset.has_access && !asset.is_creator && (
                      <span className="ev-message-card1 ev-fs-11 me-1">Shared access</span>
                    )}
                  </span>
                </div>
                );
              })}
            </>
          )}
        </div>

        <div className="d-flex justify-content-between align-items-center mb-3">
          <span className="ev-small-p-dark">
            {assetTotal > 0 ? `Showing ${assets.length} of ${assetTotal}` : ''}
          </span>
          {assetTotal > ASSET_PAGE_SIZE && (
            <div className="d-flex gap-2">
              <button
                className="ev-btn-light-fill"
                disabled={assetPage === 1 || loadingAssets}
                onClick={() => setAssetPage(assetPage - 1)}
              >
                <i className="bi bi-chevron-left"></i>
              </button>
              <button className="ev-btn-light-fill" disabled>
                Page {assetPage} of {totalPages}
              </button>
              <button
                className="ev-btn-light-fill"
                disabled={assetPage >= totalPages || loadingAssets}
                onClick={() => setAssetPage(assetPage + 1)}
              >
                <i className="bi bi-chevron-right"></i>
              </button>
            </div>
          )}
        </div>
      </div>

      <div className="col-12 col-md-5">
        <div className="ev-card-3">
          {renderTargetPicker()}

          <label className="ev-reg-p-dark mt-3 mb-1">Selected</label>
          {selectedFabrics.length === 0 && explicitThreads.length === 0 ? (
            <p className="ev-fs-11 mb-0">Nothing picked yet</p>
          ) : (
            [['PROJECT', 'Projects', selectedFabrics],
             ['DATA_COLLECTION', 'Data Folders', [
               ...linkedThreads.map(t => ({ id: t.id, name: t.name, viaProject: true })),
               ...explicitThreads
             ]]]
              .filter(([, , list]) => list.length > 0)
              .map(([view, label, list]) => (
                <div key={view} className="mb-2">
                  <span className="ev-fs-11">{label} ({list.length})</span>
                  <div className="d-flex flex-wrap gap-1 mt-1">
                    {list.map(item => (
                      <span key={item.id} className="ev-message-card1 ev-fs-11">
                        <TrimContent text={item.name} maxLength={22} />
                        {item.viaProject ? (
                          <i className="ms-1">(via project)</i>
                        ) : (
                          <button
                            type="button"
                            className="btn-close ms-1 align-middle"
                            style={{ fontSize: '0.5rem' }}
                            aria-label={`Remove ${item.name}`}
                            onClick={() => removeSelected(view, item.id)}
                          ></button>
                        )}
                      </span>
                    ))}
                  </div>
                </div>
              ))
          )}

          <p className="ev-small-p-dark mt-3 mb-3">
            <i className="bi bi-info-circle me-1"></i>
            {linkedThreadIds.length > 0 && `${linkedThreadIds.length} Data Folder(s) linked to the selected project(s) transfer too. `}
            {source.name} loses access to everything transferred.
          </p>

          <button
            type="button"
            className="ev-btn-blue-fill-round ev-bold-reg w-100"
            disabled={
              (selectedFabrics.length === 0 && explicitThreads.length === 0) ||
              !targetUserId || isSubmitting
            }
            onClick={() => submitTransfer('PARTIAL')}
          >
            {isSubmitting ? 'Transferring...' : 'Transfer Ownership'}
          </button>
        </div>
      </div>
    </div>
  );

  const renderCompleteTab = () => (
    <div className="row g-3">
      <div className="col-12 col-md-7">
        <label className="ev-reg-p-dark mb-1">Everything this user holds</label>
        <div className="ev-card-2 mb-3">
          {loadingSummary || !summary ? (
            <p className="ev-small-p-dark mb-0">
              <span className="spinner-border spinner-border-sm me-2" role="status"></span>
              Loading
            </p>
          ) : !summary.owns_anything ? (
            <p className="ev-small-p-dark mb-0">
              <i className="bi bi-check-circle me-1"></i>
              This user does not hold anything, so there is nothing to transfer.
            </p>
          ) : (
            <div className="row">
              {COUNT_ROWS.filter(([key]) => counts[key] > 0).map(([key, label]) => (
                <div key={key} className="col-12 col-sm-6 col-lg-4 mb-2 d-flex align-items-center gap-2">
                  <span className="ev-message-card1 ev-fs-11">{counts[key]}</span>
                  <span className="ev-small-p-dark">{label}</span>
                </div>
              ))}
            </div>
          )}
        </div>

        {solePmProjects.length > 0 && (
          <div className="alert alert-warning ev-fs-12 py-2">
            <i className="bi bi-exclamation-triangle me-1"></i>
            Sole Project Manager on {solePmProjects.length} project(s) &mdash;{' '}
            {solePmProjects.slice(0, 3).map(p => p.name).join(', ')}
            {solePmProjects.length > 3 && ` +${solePmProjects.length - 3} more`}. The new owner takes
            that role over.
          </div>
        )}

        {counts.country_headships > 0 && (
          <div className="alert alert-info ev-fs-12 py-2">
            <i className="bi bi-info-circle me-1"></i>
            The new owner takes over {counts.country_headships} country headship(s) and the
            countries they cover.
          </div>
        )}
      </div>

      <div className="col-12 col-md-5">
        <div className="ev-card-3">
          {renderTargetPicker()}

          <p className="ev-small-p-dark mt-3 mb-3">
            <i className="bi bi-info-circle me-1"></i>
            {targetUser
              ? `Everything above moves to ${targetUser.name}, leaving ${source.name} with nothing.`
              : 'Select the user who should take over everything.'}
          </p>

          {confirmingComplete ? (
            <div className="alert alert-danger ev-fs-12 mb-0">
              <i className="bi bi-exclamation-triangle me-1"></i>
              Move everything {source.name} holds to <b>{targetUser && targetUser.name}</b>?
              This cannot be undone from here.
              <div className="mt-2">
                <button
                  type="button"
                  className="ev-btn-red-fill-round ev-bold-reg me-2"
                  disabled={isSubmitting}
                  onClick={() => submitTransfer('COMPLETE')}
                >
                  {isSubmitting ? 'Transferring...' : 'Yes, transfer everything'}
                </button>
                <button
                  type="button"
                  className="ev-btn-blue-outline-round ev-bold-reg"
                  disabled={isSubmitting}
                  onClick={() => setConfirmingComplete(false)}
                >
                  Cancel
                </button>
              </div>
            </div>
          ) : (
            <button
              type="button"
              className="ev-btn-blue-fill-round ev-bold-reg w-100"
              disabled={!targetUserId || isSubmitting || (summary && !summary.owns_anything)}
              onClick={() => setConfirmingComplete(true)}
            >
              Transfer Everything
            </button>
          )}
        </div>
      </div>
    </div>
  );

  return (
    <div className="modal fade show d-block bg-dark bg-opacity-50">
      <div
        className="modal-dialog modal-dialog-centered modal-dialog-scrollable modal-lg"
        style={{ maxWidth: '980px' }}
      >
        <div className="modal-content">
          <div className="modal-header">
            <p className="mb-0 ev-fs-15 ev-bold-med">
              Transfer Ownership &mdash; {source.name}
              <span className="ev-message-card1 ev-fs-11 ms-2">{roleLabel(source.role)}</span>
            </p>
            <button type="button" className="btn-close" onClick={onClose}></button>
          </div>

          <div className="modal-body ev-fs-12">
            <div className="d-flex gap-2 mb-4">
              <button
                type="button"
                className={`btn ${activeTab === 'partial' ? 'ev-btn-blue-fill-round' : 'ev-btn-blue-outline-round'} ev-btn-med`}
                onClick={() => setActiveTab('partial')}
              >
                Partial Transfer
              </button>
              <button
                type="button"
                className={`btn ${activeTab === 'complete' ? 'ev-btn-blue-fill-round' : 'ev-btn-blue-outline-round'} ev-btn-med`}
                onClick={() => {
                  const picked = allTargets.find(u => u.user_id === targetUserId);
                  if (picked && !picked.can_receive_complete) {
                    setTargetUserId('');
                    setConfirmingComplete(false);
                  }
                  setActiveTab('complete');
                }}
              >
                Complete Transfer
              </button>
            </div>

            {activeTab === 'partial' ? renderPartialTab() : renderCompleteTab()}
          </div>

          <div className="modal-footer">
            <button type="button" className="btn btn-secondary ev-btn-reg" onClick={onClose}>
              Close
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default TransferOwnershipModal;
